// src/edu-world/systems/result-handler.ts
// Result_Handler: processes minigame results, triggers NPC reaction + world impact (Phase 1)

import type { Mission, MissionState, WorldImpact } from '../types';
import type { MinigameCompleteEvent } from '../types/events';
import type { EventBus } from '../core/event-bus';
import type { NPCRenderer } from './npc-renderer';
import type { DialogueBox } from '../ui/dialogue-box';

export interface ResultHandlerConfig {
  eventBus: EventBus;
  npcRenderer: NPCRenderer;
  dialogueBox: DialogueBox;
  missions: Mission[];
  impacts: WorldImpact[];
  onMissionStateChange: (missionId: string, state: MissionState) => void;
  onMissionFailure?: (missionId: string, npcId: string) => void;
}

export class ResultHandler {
  private eventBus: EventBus;
  private npcRenderer: NPCRenderer;
  private dialogueBox: DialogueBox;
  private missions: Mission[];
  private impacts: WorldImpact[];
  private onMissionStateChange: (missionId: string, state: MissionState) => void;
  private onMissionFailure?: (missionId: string, npcId: string) => void;

  constructor(config: ResultHandlerConfig) {
    this.eventBus = config.eventBus;
    this.npcRenderer = config.npcRenderer;
    this.dialogueBox = config.dialogueBox;
    this.missions = config.missions;
    this.impacts = config.impacts;
    this.onMissionStateChange = config.onMissionStateChange;
    this.onMissionFailure = config.onMissionFailure;

    // Subscribe to minigame completion
    this.eventBus.on('minigame:complete', (event) => this.handleResult(event));
  }

  private handleResult(event: MinigameCompleteEvent): void {
    const mission = this.missions.find((m) => m.id === event.mission_id);
    if (!mission) {
      console.warn(`[ResultHandler] Mission not found: ${event.mission_id}`);
      return;
    }

    if (event.success) {
      this.handleSuccess(mission, event);
    } else {
      this.handleFailure(mission);
    }
  }

  private handleSuccess(mission: Mission, _event: MinigameCompleteEvent): void {
    // 1. Update mission state to "completed" (within 500ms)
    mission.state = 'completed';
    this.onMissionStateChange(mission.id, 'completed');

    // 2. Trigger NPC emotional reaction (jump + gratitude)
    this.npcRenderer.playJumpHappy(mission.npc_id);

    // Show gratitude dialogue after brief delay
    setTimeout(() => {
      this.dialogueBox.show({
        npcName: 'Rosa', // TODO: resolve from NPC config
        text: mission.emotional_reaction.gratitude_dialogue,
      });

      // 3. After reaction (2s), emit world impact
      setTimeout(() => {
        this.dialogueBox.hide();
        this.emitWorldImpact(mission.world_impact_ref, mission.id);
      }, 2000);
    }, 500);
  }

  private handleFailure(mission: Mission): void {
    // Keep mission active (no state change)
    // Show encouragement
    this.npcRenderer.playBeckonAnimation(mission.npc_id);

    // Phase 2: Notify orchestrator of failure (for audio encouragement)
    if (this.onMissionFailure) {
      this.onMissionFailure(mission.id, mission.npc_id);
    }

    setTimeout(() => {
      this.dialogueBox.show({
        npcName: 'Rosa',
        text: 'Vamos tentar outra vez!',
      });

      // Hide after 3 seconds
      setTimeout(() => {
        this.dialogueBox.hide();
      }, 3000);
    }, 300);
  }

  private emitWorldImpact(impactRef: string, missionId: string): void {
    const impact = this.impacts.find((i) => i.id === impactRef);
    if (!impact) {
      console.warn(`[ResultHandler] WorldImpact not found: ${impactRef}`);
      return;
    }

    this.eventBus.emit('world:impact', {
      impact_id: impact.id,
      mission_id: missionId,
    });
  }
}
